import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { PRODUCTS } from "../data/content";
import { EnquiryError, submitEnquiry } from "../services/forms";
import { businessEmailSchema, captchaConfigured, captchaSchema, phoneSchema } from "../services/fieldRules";
import { trackEvent, EVENTS } from "../services/analytics";
import { TurnstileField } from "./TurnstileField";

const schema = z.object({
  fullName: z.string().min(2, "Please enter your full name").max(100),
  businessEmail: businessEmailSchema,
  company: z.string().min(2, "Please enter your company name").max(120),
  phone: phoneSchema,
  product: z.string().optional(),
  message: z.string().min(10, "Please tell us a little about your requirement").max(2000),
  consent: z.literal(true, { errorMap: () => ({ message: "Please accept the privacy acknowledgement" }) }),
  captchaToken: captchaSchema,
});

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-blue-600";

const Field = ({ label, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold text-slate-700">
      {label}
    </label>
    {children}
    {error && (
      <p className="mt-1 text-xs text-red-600" role="alert" data-testid={`${htmlFor}-error`}>
        {error.message}
      </p>
    )}
  </div>
);

const SERVER_FIELDS = ["fullName", "businessEmail", "company", "phone", "product", "message", "consent", "captcha"];

export const EnquiryForm = ({ defaultProduct = "" }) => {
  const [phase, setPhase] = useState("form");
  const [referenceId, setReferenceId] = useState(null);
  const location = useLocation();
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { product: defaultProduct, consent: false, captchaToken: "" },
  });

  const onSubmit = async (values) => {
    if (!captchaConfigured) {
      setError("captchaToken", { type: "setup", message: "The security check is not set up yet. Nothing was sent." });
      setPhase("form");
      return;
    }
    setPhase("sending");
    try {
      const res = await submitEnquiry({
        type: "general",
        ...values,
        product: values.product || null,
        sourcePage: location.pathname,
        campaign: new URLSearchParams(window.location.search).get("utm_campaign"),
      });
      setReferenceId(res.referenceId);
      setPhase("success");
      trackEvent(EVENTS.ENQUIRY_SUBMIT, { product: values.product || "general" });
    } catch (err) {
      if (err instanceof EnquiryError && err.code === "VALIDATION" && err.errors) {
        const mapped = SERVER_FIELDS.some((field) => {
          if (!err.errors[field]) return false;
          const formField = field === "captcha" ? "captchaToken" : field;
          setError(formField, { type: "server", message: err.errors[field] });
          return true;
        });
        setPhase(mapped ? "form" : "error");
        return;
      }
      setPhase("error");
    }
  };

  if (phase === "success") {
    return (
      <div className="card-line p-8 text-center" data-testid="enquiry-success-state">
        <CheckCircle2 className="mx-auto text-teal-600" size={44} />
        <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Enquiry received</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
          Thank you — your enquiry has been recorded with reference{" "}
          <span className="font-mono text-xs font-semibold text-slate-900">{referenceId}</span>.
          The right team will respond to you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="card-line space-y-4 p-6 sm:p-8" data-testid="enquiry-form">
      {phase === "error" && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="enquiry-error-state">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <span>
            Your enquiry could not be submitted and nothing was sent. Your details are
            still in the form — please try again.
          </span>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name *" htmlFor="enquiry-full-name" error={errors.fullName}>
          <input id="enquiry-full-name" className={inputCls} autoComplete="name" {...register("fullName")} data-testid="enquiry-full-name-input" />
        </Field>
        <Field label="Business email *" htmlFor="enquiry-email" error={errors.businessEmail}>
          <input id="enquiry-email" type="email" className={inputCls} autoComplete="email" {...register("businessEmail")} data-testid="enquiry-email-input" />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Company name *" htmlFor="enquiry-company" error={errors.company}>
          <input id="enquiry-company" className={inputCls} autoComplete="organization" {...register("company")} data-testid="enquiry-company-input" />
        </Field>
        <Field label="Phone number *" htmlFor="enquiry-phone" error={errors.phone}>
          <input id="enquiry-phone" type="tel" className={inputCls} autoComplete="tel" placeholder="+91 98765 43210" {...register("phone")} data-testid="enquiry-phone-input" />
        </Field>
      </div>
      <Field label="Product of interest (optional)" htmlFor="enquiry-product">
          <select id="enquiry-product" className={inputCls} {...register("product")} data-testid="enquiry-product-select">
            <option value="">General enquiry</option>
            {PRODUCTS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      <Field label="How can we help? *" htmlFor="enquiry-message" error={errors.message}>
        <textarea id="enquiry-message" rows={5} className={inputCls} {...register("message")} data-testid="enquiry-message-input" />
      </Field>
      <div>
        <label className="flex items-start gap-2.5 text-xs text-slate-600" htmlFor="enquiry-consent">
          <input
            id="enquiry-consent"
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
            {...register("consent")}
            data-testid="enquiry-consent-checkbox"
          />
          <span>
            I agree that ZasyaOnline may use these details to respond to my enquiry, as
            described in the Privacy Policy.
          </span>
        </label>
        {errors.consent && (
          <p className="mt-1 text-xs text-red-600" role="alert" data-testid="enquiry-consent-error">
            {errors.consent.message}
          </p>
        )}
      </div>
      <TurnstileField
        resetKey={phase}
        error={errors.captchaToken?.message}
        onToken={(token) => setValue("captchaToken", token, { shouldValidate: true })}
      />
      <button
        type="submit"
        className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        disabled={phase === "sending"}
        data-testid="enquiry-form-submit-button"
      >
        {phase === "sending" ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Submitting…
          </>
        ) : phase === "error" ? (
          <>
            <RotateCcw size={15} /> Try again
          </>
        ) : (
          "Send enquiry"
        )}
      </button>
    </form>
  );
};
